import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import RadioService from "services/radio/RadioService";
import { FaPlay, FaCrown } from "react-icons/fa";
import { HiArrowLeft } from "react-icons/hi";
import { addStationToRadio } from "redux/AudioPlayerRedux";
import { useDispatch, useSelector } from "react-redux";
import AddRadioToFav from "./shared/AddRadioToFav";
import LikeService from "services/music/LikeService";
import { Link } from "react-router-dom";
import { Modal } from "antd";
import { RouteName } from "constant/routeNames";
import DescriptionSkeletion from "./skeleton/DescriptionSkeletion";
import { Helmet } from "react-helmet-async";

function RadioDescriptionPage() {
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [radio, setRadio] = useState<any>();
  const [radioCategory, setRadioCategory] = useState<any>();
  const [isLoading, setIsLoading] = useState<any>(false);
  const [favorite, setFavorite] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { currentRadio, isPlaying } = useSelector(
    (state: any) => state?.audio
  );
  const isPremiumContent = radio?.plan_id === 2;
  const isPlusUser = loggedInUser?.is_subscribed;
  const isThisPlaying =
    isPlaying &&
    currentRadio?.id != null &&
    currentRadio?.id === radio?.id;

  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  // get radio
  useEffect(() => {
    setIsLoading(true);
    RadioService.getRadioBySlug(slug)
      .then((res) => {
        setRadio(res.data);
        setRadioCategory(res?.data?.category[0]);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [slug]);

  // favorites
  useEffect(() => {
    if (loggedInUser) {
      LikeService.getFavoriteById(loggedInUser?.id, loggedInUser?.id)
        .then((res) => {
          const favoritesIds = res.profile.favorite_radio_stations.map(
            (item: any) => item.id
          );
          setFavorite(favoritesIds);
        })
        .catch(() => {});
    }
  }, [loggedInUser]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleRadio = () => {
    if (isPremiumContent && !isPlusUser) {
      setIsModalOpen(true);
      return;
    }
    dispatch(addStationToRadio(radio));
  };

  return (
    <> 
      <Helmet>
        <title>{radio?.title ? `${radio?.title} - AYOZAT` : "AYOZAT"}</title>
        <meta name="title" content={radio?.title} />
        <meta name="description" content={radio?.description} />
        <link rel="canonical" href={`${FE_URL}/radio/${radio?.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={radio?.title} />
        <meta name="twitter:description" content={radio?.description} />
        <meta name="twitter:image" content={radio?.artwork_url} />
        <meta name="twitter:url" content={`${FE_URL}/radio/${radio?.slug}`} />
        <meta property="og:title" content={radio?.title} />
        <meta property="og:description" content={radio?.description} />
        <meta property="og:image" content={radio?.artwork_url} />
        <meta property="og:url" content={`${FE_URL}/radio/${radio?.slug}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      {!isLoading && radio ? (
        <div className="relative min-h-screen overflow-hidden text-white max-sm:-mx-3 max-sm:w-[calc(100%+1.5rem)]">
          {/* cinematic backdrop */}
          <div
            className="absolute inset-0 scale-110 bg-cover bg-center blur-[80px] opacity-70"
            style={{ backgroundImage: `url(${radio?.artwork_url})` }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/70 via-[#0a0a0c]/85 to-[#0a0a0c]" />

          {/* content */}
          <div className="relative z-10 mx-auto max-w-4xl px-6 pb-20 pt-6 max-sm:px-4">
            {/* back */}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
              aria-label="Go back"
            >
              <HiArrowLeft className="text-xl" />
            </button>

            {/* hero */}
            <div className="mt-8 flex flex-col items-center text-center">
              {/* artwork */}
              <div className="relative h-[260px] w-[260px] overflow-hidden rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] ring-1 ring-white/10 max-sm:h-[200px] max-sm:w-[200px]">
                {radio?.artwork_url && (
                  <img
                    src={radio.artwork_url}
                    alt={radio?.title}
                    className="h-full w-full object-cover"
                  />
                )}
                {/* LIVE badge */}
                <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--red,#F50537)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-lg">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                  Live
                </span>
                {/* now-playing equalizer */}
                {isThisPlaying && (
                  <div className="absolute inset-x-0 bottom-0 flex h-16 items-end justify-center gap-[3px] bg-gradient-to-t from-black/70 to-transparent pb-4">
                    <span className="radio-eq-bar" style={{ animationDelay: "0s" }} />
                    <span className="radio-eq-bar" style={{ animationDelay: "0.15s" }} />
                    <span className="radio-eq-bar" style={{ animationDelay: "0.3s" }} />
                    <span className="radio-eq-bar" style={{ animationDelay: "0.45s" }} />
                    <span className="radio-eq-bar" style={{ animationDelay: "0.6s" }} />
                  </div>
                )}
              </div>

              {/* category */}
              {radioCategory && (
                <Link
                  to={RouteName.Radio.replace(
                    "*",
                    RouteName.RadioChannelsByCategory.replace(
                      ":slug",
                      radioCategory?.alt_name
                    )
                  )}
                  className="mt-6"
                >
                  <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-white/80 backdrop-blur-sm transition-colors hover:bg-white/20">
                    {radioCategory?.name.replace("amp;", "")}
                  </span>
                </Link>
              )}

              {/* title */}
              <h1
                className="mt-4 font-secular text-5xl font-bold leading-tight text-[#FAFAFA] drop-shadow-[0_2px_14px_rgba(0,0,0,0.6)] max-md:text-4xl max-sm:text-3xl"
                dangerouslySetInnerHTML={{ __html: radio?.title }}
              />

              {/* actions */}
              <div className="mt-8 flex items-center gap-4">
                <button
                  onClick={handleRadio}
                  className="flex items-center justify-center gap-2 rounded-full bg-[var(--red,#F50537)] px-9 py-3.5 text-lg font-semibold text-white shadow-[0_10px_30px_rgba(245,5,55,0.35)] transition-opacity hover:opacity-90 max-sm:px-7 max-sm:text-base"
                >
                  {isPremiumContent && !isPlusUser ? (
                    <>
                      <FaCrown /> Subscribe to Play
                    </>
                  ) : isThisPlaying ? (
                    <>
                      <span className="flex h-4 items-end gap-[2px]">
                        <span className="radio-eq-bar" style={{ animationDelay: "0s" }} />
                        <span className="radio-eq-bar" style={{ animationDelay: "0.2s" }} />
                        <span className="radio-eq-bar" style={{ animationDelay: "0.4s" }} />
                      </span>
                      Playing
                    </>
                  ) : (
                    <>
                      <FaPlay /> Play
                    </>
                  )}
                </button>

                {/* <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-xl backdrop-blur-md transition-colors hover:bg-white/10">
                  <AddRadioToFav station={radio} favorite={favorite} />
                </span> */}
              </div>
            </div>

            {/* about */}
            {radio?.description && (
              <div className="mx-auto mt-14 max-w-2xl">
                <h2 className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.2em] text-white/40">
                  About this station
                </h2>
                <div
                  className="text-center text-sm leading-relaxed text-white/60"
                  dangerouslySetInnerHTML={{ __html: radio?.description }}
                />
              </div>
            )}
          </div>

          {/* equalizer animation */}
          <style>{`
            .radio-eq-bar {
              width: 3px;
              height: 100%;
              border-radius: 2px;
              background: #fff;
              transform-origin: bottom;
              animation: radioEq 0.9s ease-in-out infinite;
            }
            @keyframes radioEq {
              0%, 100% { transform: scaleY(0.3); }
              50% { transform: scaleY(1); }
            }
          `}</style>
        </div>
      ) : (
        <DescriptionSkeletion />
      )}

      {/* Premium modal */}
      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        className="premium-modal"
        styles={{
          content: { backgroundColor: "#111", border: "1px solid #333" },
        }}
      >
        <div className="flex flex-col items-center justify-center p-6 text-center">
          <div className="mb-4 rounded-full bg-[var(--red)]/10 p-4">
            <FaCrown className="text-4xl text-[var(--red)]" />
          </div>
          <h3 className="mb-2 text-2xl font-bold text-white">
            Premium Content
          </h3>
          <p className="mb-6 text-gray-400">
            This radio station is exclusively available for premium subscribers.
            Upgrade your plan to gain full access.
          </p>
          <div className="flex w-full gap-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="flex-1 rounded-lg border border-gray-600 px-4 py-2 font-semibold text-gray-300 transition hover:bg-[#222]"
            >
              Maybe Later
            </button>
            <Link
              to={RouteName.SubscriptionPlanRoute}
              className="flex flex-1 items-center justify-center rounded-lg bg-[var(--red)] px-4 py-2 font-semibold text-white shadow-md transition hover:opacity-90"
            >
              View Plans
            </Link>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default RadioDescriptionPage;